"use client";

import React from "react";
import { UserCheck, MessageSquare, GitBranch, Layers, ArrowRight, ShieldCheck, Compass } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function DirectConsultant() {
  const directPillars = [{
    icon: UserCheck,
    title: "Direct personal involvement",
    description: "You work directly with me, Yassine, the independent consultant who analyses your catalogue data, audits your platform, and shapes your ecommerce SEO strategy. There are no account manager layers and no junior delegates learning on your store."
  }, {
    icon: MessageSquare,
    title: "Plain-English reporting",
    description: "Every recommendation comes in clear, jargon-free English with a direct explanation of why it matters commercially. You receive practical priorities tied to product visibility, indexing health, and commercial impact rather than vanity search metrics."
  }, {
    icon: Compass,
    title: "Direct and responsive communication",
    description: "A single senior point of contact ensures faster decisions and direct feedback. Whether clarifying tag filter rules or coordinating with your development team, you discuss technical challenges directly with the person doing the work."
  }, {
    icon: Layers,
    title: "Flexible engagement models",
    description: "Engage my services in the way that best matches your internal capacity: a comprehensive one-off audit, dedicated project-based support such as platform migration guidance, or ongoing monthly consulting."
  }];
  const engagementModels = [{
    number: "01",
    name: "One-off ecommerce SEO audit",
    summary: "A comprehensive review of technical health, catalogue hierarchy, faceted filters, and product page optimisation, delivered as a prioritised roadmap."
  }, {
    number: "02",
    name: "Project-based store support",
    summary: "Targeted support for critical initiatives such as platform migrations, catalogue restructuring, or focused keyword-to-page mapping projects."
  }, {
    number: "03",
    name: "Ongoing monthly consulting",
    summary: "Continuous technical monitoring, regular Search Console and revenue review cycles, and ongoing optimisation of key collection and product pages."
  }];
  return <section id="directconsultant" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
            Direct Specialist Access
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Why work directly with an independent ecommerce SEO consultant
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ecommerce SEO requires technical precision and deep catalogue understanding. Working directly with an independent consultant gives you focused attention, agile decision-making, and full accountability on every recommendation.
          </p>
        </div>

        {/* 4 Core Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {directPillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <Card key={`pillar-${i}`} data-index={i} className="border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-semibold text-foreground font-default">
                    {pillar.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-default">
                    {pillar.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Flexible Engagement Overview */}
        <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-8 lg:p-10 mb-14 shadow-xs">
          <div className="max-w-3xl mb-8">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              Engagement options tailored to your store
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Every online store operates at a distinct stage of growth with differing internal resources. I adapt to your team and platform setup across three clear engagement formats:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {engagementModels.map((model, i) => <div key={`model-${i}`} data-index={i} className="p-5 rounded-lg border border-border bg-background/70 flex flex-col justify-between">
                <div>
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-primary/10 text-primary">
                    {model.number}
                  </span>
                  <h4 className="mt-3 text-base font-semibold text-foreground font-default">
                    {model.name}
                  </h4>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-default">
                    {model.summary}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Monthly SEO Services Link */}
          <div className="mt-8 pt-6 border-t border-border/60 flex items-center justify-between flex-wrap gap-4">
            <span className="text-sm text-muted-foreground">
              Interested in continuous strategy and technical monitoring for your store?
            </span>
            <Button asChild variant="outline" size="sm" className="rounded-full">
              <Link to="/MonthlySeoServicesPage" className="inline-flex items-center gap-1.5">
                <span>View Monthly SEO Services</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Agency vs Independent Consultant note (exactly one short neutral sentence with hire an SEO consultant link) */}
        <div className="pt-8 border-t border-border/60 text-center max-w-3xl mx-auto">
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            If you are weighing an agency against an independent specialist, you can review considerations on how to{" "}
            <Link to="/HireSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium inline-flex items-center gap-1">
              hire an SEO consultant
              <ArrowRight className="size-3.5" />
            </Link>{" "}
            to determine which setup best suits your catalogue complexity and commercial goals.
          </p>
        </div>
      </div>
    </section>;
}